import React, { useState } from 'react';
import '@/App.css';
import { Phone, Mail, Download, Award, TrendingUp, Users, Target, ChevronRight, Linkedin, Mountain, Music, Dumbbell, Compass, Tent, Globe } from 'lucide-react';

function App() {
  const [activeSection, setActiveSection] = useState('home');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const achievements = [
    { title: 'Customer Satisfaction Top 10', count: '11x', icon: '🏆' },
    { title: '100% Quality Assurance Award', count: '21x', icon: '⭐' },
    { title: 'Top 25 Conversion Rate', count: '7x', icon: '📈' },
    { title: 'Top 10 Overall KPI', count: '5x', icon: '🎯' },
  ];

  const stats = [
    { label: 'Revenue Generated', value: '$7.3M+', icon: <TrendingUp className="w-8 h-8" /> },
    { label: 'Calls Made', value: '38,000+', icon: <Phone className="w-8 h-8" /> },
    { label: 'Client Enrollments', value: '1,000+', icon: <Users className="w-8 h-8" /> },
    { label: 'Advertising Analyzed', value: '$6M+', icon: <Target className="w-8 h-8" /> },
  ];

  const experience = [
    {
      company: 'National Debt Relief',
      role: 'Account Executive',
      period: '2023 - Present',
      location: 'Henderson, NV',
      highlights: [
        '#1 Agent Rank - conversion + cancellation + deal size (2025)',
        '#2 Producer - $5.6M in Q4 2025',
        'MVP Hall of Fame (2x)',
        'Promoted to Co-Captain within 3 months',
        '$2M Dollar Club (Exclusive)',
        '$1M Club 1st Month on Floor',
        'Legend Status - 1,000 enrollments'
      ]
    },
    {
      company: 'Logical Position',
      role: 'Business Development Executive',
      period: '2021 - 2023',
      location: 'Henderson, NV',
      highlights: [
        '#1 Fastest to BDE promotion record',
        'Rookie of the Quarter',
        'Created Live Call Shadowing Thursdays',
        '$1.7M+ revenue generated',
        '38,000+ calls made'
      ]
    },
    {
      company: 'CHAMPS Trade Show',
      role: 'Trade Show Sales Representative',
      period: '2022',
      location: 'Las Vegas, NV',
      highlights: [
        '#2 Producer with zero product knowledge',
        '$52K sold during trade show',
        '$45K from follow-ups in 3 months'
      ]
    }
  ];

  const personalInterests = [
    { icon: <Mountain className="w-6 h-6" />, title: 'Hiking & Outdoors', description: 'Exploring trails and connecting with nature' },
    { icon: <Globe className="w-6 h-6" />, title: 'Travel & Adventure', description: 'Discovering new places and cultures' },
    { icon: <Dumbbell className="w-6 h-6" />, title: 'Fitness & Nutrition', description: 'Maintaining peak physical performance' },
    { icon: <Tent className="w-6 h-6" />, title: 'Camping', description: 'Disconnecting to recharge outdoors' },
    { icon: <Target className="w-6 h-6" />, title: 'Pickleball', description: 'Competitive spirit on the court' },
    { icon: <Music className="w-6 h-6" />, title: 'Music', description: 'Finding rhythm in work and life' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold text-[#0A2463]">Cameron Geisser</h1>
            </div>
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-[#0A2463] font-medium transition-colors">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-[#0A2463] font-medium transition-colors">About</button>
              <button onClick={() => scrollToSection('experience')} className="text-gray-700 hover:text-[#0A2463] font-medium transition-colors">Experience</button>
              <button onClick={() => scrollToSection('achievements')} className="text-gray-700 hover:text-[#0A2463] font-medium transition-colors">Achievements</button>
              <button onClick={() => scrollToSection('personal')} className="text-gray-700 hover:text-[#0A2463] font-medium transition-colors">Beyond Sales</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-[#0A2463] font-medium transition-colors">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-16 bg-gradient-to-br from-[#0A2463] via-[#1e3a8a] to-[#0A2463] text-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-white/10 rounded-full text-sm font-medium">
                Elite Sales Professional
              </div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                Transforming Challenges Into
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white">
                  Record-Breaking Results
                </span>
              </h1>
              <p className="text-xl text-blue-100">
                Multi-award winning sales executive with $7.3M+ in revenue generated. Consistently ranked #1 in conversion, customer satisfaction, and profitability.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <a 
                  href="https://customer-assets.emergentagent.com/job_hello-hub-152/artifacts/d5810690_final_resume.pdf" 
                  download
                  className="inline-flex items-center px-6 py-3 bg-white text-[#0A2463] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl"
                  data-testid="download-resume-button"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Resume
                </a>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="inline-flex items-center px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-[#0A2463] transition-all"
                  data-testid="contact-me-button"
                >
                  Let's Connect
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-2xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1599090738077-75d2187fd892?w=800&q=80" 
                alt="Professional success"
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A2463] text-white rounded-full mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-bold text-[#0A2463] mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_hello-hub-152/artifacts/svuicrtb_IMG_8631.heic" 
                alt="Cameron Geisser professional photo"
                className="rounded-2xl shadow-xl w-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-[#0A2463]">About Me</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                I am consistently among the top performers in my sales organization, achieving countless awards in efficiency, conversion, and profitability. My success comes early and often due to my coachability, eagerness to learn quickly, and adaptability.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Beyond being deemed among the best, I actively help organizations solve internal problems and innovate new systems to enhance workflow and business capacity.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                I'm seeking opportunities that align with my ambition for continuous income growth and personal development, breaking through the ceiling of traditional sales roles.
              </p>
              <div className="pt-4 space-y-1">
                <p className="text-gray-700">⚾ <span className="font-semibold">Former College Baseball Player</span> - Augustana College</p>
                <p className="text-gray-600 text-sm ml-6">Teamwork, discipline, and competitive drive fuel my success</p>
              </div>
              <div className="pt-4">
                <h3 className="text-xl font-semibold text-[#0A2463] mb-3">Education</h3>
                <div className="space-y-2">
                  <p className="text-gray-700"><span className="font-semibold">B.S. International Business - Economics</span></p>
                  <p className="text-gray-700"><span className="font-semibold">B.A. Spanish</span></p>
                  <p className="text-gray-600">Augustana College, 2020</p>
                </div>
              </div>
              <div className="pt-4">
                <h3 className="text-xl font-semibold text-[#0A2463] mb-3">Certifications</h3>
                <div className="space-y-2">
                  <p className="text-gray-700">• IAPDA Certified Debt Specialist (2026)</p>
                  <p className="text-gray-700">• Google Ads/Analytics (2023)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 bg-gray-50" data-testid="experience-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A2463] mb-4">Career Journey</h2>
            <p className="text-xl text-gray-600">A track record of rapid advancement and exceptional results</p>
          </div>
          <div className="space-y-12">
            {experience.map((job, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow" data-testid={`experience-${index}`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-[#0A2463] mb-2">{job.role}</h3>
                    <p className="text-xl text-gray-700 font-semibold">{job.company}</p>
                    <p className="text-gray-600">{job.location}</p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <span className="inline-block px-4 py-2 bg-[#0A2463] text-white rounded-full font-medium">
                      {job.period}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  {job.highlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-start">
                      <ChevronRight className="w-5 h-5 text-[#0A2463] mr-2 mt-1 flex-shrink-0" />
                      <p className="text-gray-700">{highlight}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section id="achievements" className="py-20 bg-white" data-testid="achievements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A2463] mb-4">Award-Winning Performance</h2>
            <p className="text-xl text-gray-600">Recognition for consistent excellence</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {achievements.map((achievement, index) => (
              <div key={index} className="bg-gradient-to-br from-[#0A2463] to-[#1e3a8a] rounded-xl p-8 text-white text-center hover:scale-105 transition-transform" data-testid={`achievement-${index}`}>
                <div className="text-5xl mb-4">{achievement.icon}</div>
                <div className="text-4xl font-bold mb-2">{achievement.count}</div>
                <div className="text-lg font-medium">{achievement.title}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-[#0A2463] mb-6">Leadership & Innovation</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Award className="w-5 h-5 text-[#0A2463] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Co-Captain (Assistant Manager) promotion within 3 months</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-5 h-5 text-[#0A2463] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Rookie Mentor for new-hire development</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-5 h-5 text-[#0A2463] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Created Live Call Shadowing Thursdays program</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-5 h-5 text-[#0A2463] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">#1 Fastest to BDE promotion record</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-[#0A2463] mb-6">Elite Recognitions</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Award className="w-5 h-5 text-[#0A2463] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">MVP Hall of Fame (2x)</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-5 h-5 text-[#0A2463] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Legend Status - 1,000 enrollments</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-5 h-5 text-[#0A2463] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Exclusive $2M Dollar Club member</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-5 h-5 text-[#0A2463] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Best Month Ever Challenge Winner</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-12 text-center">
            <img 
              src="https://images.unsplash.com/photo-1612441804231-77a36b284856?w=1200&q=80" 
              alt="Mountain landscape representing peak performance"
              className="rounded-2xl shadow-xl w-full max-w-3xl mx-auto object-cover h-64"
            />
          </div>
        </div>
      </section>

      {/* Beyond Sales - Personal Section */}
      <section id="personal" className="py-20 bg-gray-50" data-testid="personal-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A2463] mb-4">Beyond Sales</h2>
            <p className="text-xl text-gray-600">Balancing high performance with an active, adventurous lifestyle</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_hello-hub-152/artifacts/x8cvdykz_IMG_5972.heic" 
                alt="Cameron kayaking"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="text-2xl font-bold mb-2">Embracing New Experiences</h3>
                  <p className="text-blue-100">Always eager to try new adventures and challenges</p>
                </div>
              </div>
            </div>
            <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden shadow-xl">
              <video 
                src="https://customer-assets.emergentagent.com/job_hello-hub-152/artifacts/anf9774j_ScreenRecording_03-08-2026_23-44-58_1_copy_copy_copy.mov"
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                data-testid="lifestyle-video"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="text-2xl font-bold mb-2">Active Lifestyle</h3>
                  <p className="text-blue-100">Exploring Henderson on two wheels</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-8">
              {personalInterests.map((interest, index) => (
                <div key={index} className="text-center" data-testid={`interest-${index}`}>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A2463] text-white rounded-full mb-4">
                    {interest.icon}
                  </div>
                  <h3 className="text-lg font-bold text-[#0A2463] mb-2">{interest.title}</h3>
                  <p className="text-gray-600">{interest.description}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 pt-8 border-t border-gray-200 text-center">
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Just as I bring discipline and competitive drive from my college baseball days to the sales floor, 
                I maintain that same energy through fitness, outdoor adventures, and exploring new places. 
                This balanced approach keeps me sharp, motivated, and ready to tackle any challenge.
              </p>
            </div>
          </div>

          {/* Family & Home Section */}
          <div className="mt-16 bg-gradient-to-br from-[#0A2463] to-[#1e3a8a] rounded-2xl shadow-xl p-8 md:p-12 text-white">
            <h3 className="text-3xl font-bold text-center mb-8">Family</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="relative rounded-xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hello-hub-152/artifacts/lnpkukk1_IMG_5754.heic" 
                  alt="Family time"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hello-hub-152/artifacts/loijr7mo_IMG_8078.heic" 
                  alt="Family bowling outing"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hello-hub-152/artifacts/c8ggoqnr_IMG_9286.heic" 
                  alt="Home office companions"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <p className="text-center text-blue-100 mt-8 text-lg">
              Family and my office companions keep me grounded. They remind me why I push for excellence every day.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-[#0A2463] via-[#1e3a8a] to-[#0A2463] text-white" data-testid="contact-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Let's Work Together</h2>
          <p className="text-xl text-blue-100 mb-12">
            Ready to break through your sales ceiling? I'm seeking opportunities to continue scaling my impact and income. Let's connect and discuss how I can drive exceptional results for your organization.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <a 
              href="tel:+18157900295" 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-xl p-6 transition-all hover:scale-105"
              data-testid="phone-contact"
            >
              <Phone className="w-8 h-8 mx-auto mb-4" />
              <div className="font-semibold mb-2">Call Me</div>
              <div className="text-blue-100">(815) 790-0295</div>
            </a>
            <a 
              href="mailto:ccgeisser77@gmail.com" 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-xl p-6 transition-all hover:scale-105"
              data-testid="email-contact"
            >
              <Mail className="w-8 h-8 mx-auto mb-4" />
              <div className="font-semibold mb-2">Email Me</div>
              <div className="text-blue-100">ccgeisser77@gmail.com</div>
            </a>
            <a 
              href="https://linkedin.com/in/camerongeisser" 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-xl p-6 transition-all hover:scale-105"
              data-testid="linkedin-contact"
            >
              <Linkedin className="w-8 h-8 mx-auto mb-4" />
              <div className="font-semibold mb-2">LinkedIn</div>
              <div className="text-blue-100">Connect with me</div>
            </a>
          </div>
          <a 
            href="https://customer-assets.emergentagent.com/job_hello-hub-152/artifacts/d5810690_final_resume.pdf" 
            download
            className="inline-flex items-center px-8 py-4 bg-white text-[#0A2463] rounded-lg font-bold text-lg hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl"
            data-testid="download-resume-footer"
          >
            <Download className="w-6 h-6 mr-3" />
            Download Full Resume
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2463] text-white py-8" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-blue-200">
            © 2025 Cameron Geisser. All rights reserved. | Henderson, NV 89052
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;